import { useEffect, useRef, useState } from 'react';
import { localNow, type DayNutrition, type HomeSummary, type Meal, type User, type Workout } from '@jungles/shared';
import { api } from '../lib/api';
import { foodQuickCopy, nextHomeMeal, workoutQuickCopy, type HomeWorkouts, type QuickCopy } from '../lib/home-quick';
import '../styles/home-quick.css';

type Action = { key: string; copy: QuickCopy; run: () => void; blocked?: boolean };
function QuickCard({ action, kind, onHold }: { action: Action; kind: 'workout' | 'food'; onHold: (held: boolean) => void }) {
  const [held, setHeld] = useState<Action | null>(null);
  const cancelled = useRef(false);
  const pointerStart = useRef({ x: 0, y: 0 });
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const shown = held ?? action;
  const release = () => { clearTimeout(timer.current); timer.current = setTimeout(() => { setHeld(null); onHold(false); }, 350); };
  useEffect(() => () => clearTimeout(timer.current), []);
  return <button type="button" className={`qk home-qk ${kind}${shown.copy.hot ? ' hot' : ''}`}
    aria-label={`${shown.copy.title.replace('\n', ' ')}. ${shown.copy.detail}${shown.copy.accent ?? ''}${shown.copy.suffix ?? ''}`}
    onPointerDown={e => {
      cancelled.current = false;
      pointerStart.current = { x: e.clientX, y: e.clientY };
      onHold(true);
      clearTimeout(timer.current);
      setHeld(action);
      e.currentTarget.setPointerCapture(e.pointerId);
    }}
    onPointerUp={release}
    onPointerMove={e => { if (Math.hypot(e.clientX - pointerStart.current.x, e.clientY - pointerStart.current.y) > 10) cancelled.current = true; }}
    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') cancelled.current = false; }}
    onPointerCancel={() => { cancelled.current = true; release(); }}
    onLostPointerCapture={release}
    onClick={e => {
      // Keep the same physical button through revalidation and its compatibility click.
      // A changed/deleted draft or a day/meal transition consumes, never redirects, this gesture.
      if (!cancelled.current && !action.blocked && shown.key === action.key) action.run();
      else e.preventDefault();
      release();
    }}>
    <b title={shown.copy.title}>{shown.copy.title.replace('Продолжить', 'Продол\u00adжить').replace('тренировку', 'трени\u00adровку').replace('тренировка', 'трени\u00adровка')}</b>
    <span className="st">{shown.copy.detail}{shown.copy.accent ? <em>{shown.copy.accent}</em> : null}{shown.copy.suffix}</span>
    <img src={`/assets/ui/tab-${kind === 'workout' ? 'workouts' : 'food'}.webp`} alt="" />
  </button>;
}

export function HomeQuickButtons({ user, home, summary, draft, summaryLoading, summaryError, onWorkout, onFood, onNewDay, onHold }: {
  user: User; home: HomeSummary; summary: HomeWorkouts | null; draft: Workout | null;
  summaryLoading: boolean; summaryError: boolean;
  onWorkout: (draft: Workout | null) => void;
  onFood: (meal: Meal, date: string) => void;
  onNewDay: () => void;
  onHold: (held: boolean) => void;
}) {
  const [now, setNow] = useState(() => localNow(user.timezone));
  const [nutrition, setNutrition] = useState<DayNutrition | null>(null);
  const [nonce, setNonce] = useState(0);
  const lastDate = useRef(now.date);
  const newDay = useRef(onNewDay); newDay.current = onNewDay;
  useEffect(() => {
    const tick = () => {
      const next = localNow(user.timezone);
      if (lastDate.current !== next.date) { lastDate.current = next.date; newDay.current(); }
      setNow(prev => prev.time === next.time && prev.date === next.date ? prev : next);
    };
    const refresh = () => { if (document.visibilityState === 'visible') { tick(); setNonce(n => n + 1); } };
    tick();
    const id = setInterval(tick, 1000);
    window.addEventListener('focus', refresh);document.addEventListener('visibilitychange', refresh);
    return () => { clearInterval(id);window.removeEventListener('focus', refresh);document.removeEventListener('visibilitychange', refresh); };
  }, [user.timezone]);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    api.get<{ day: DayNutrition }>(`/api/nutrition/day?date=${now.date}`, controller.signal)
      .then(value => { if (active) setNutrition(value.day?.date === now.date ? value.day : null); })
      .catch(() => { if (active) setNutrition(null); });
    return () => { active = false; controller.abort(); };
  }, [now.date, nonce]);
  const day = nutrition?.date === now.date ? nutrition : null;
  const meal = nextHomeMeal(now.time, day);
  const validSummary = !summaryError ? summary : null;
  const validDraft = !summaryError ? draft : null;
  const workout: Action = {
    key: validDraft ? `draft:${validDraft.id}` : 'new',
    copy: workoutQuickCopy(validDraft, validSummary, now.date, home.today === now.date ? home.week : null),
    blocked: summaryLoading,
    run: () => onWorkout(validDraft),
  };
  const food: Action = {
    key: `${now.date}:${meal}`,
    copy: foodQuickCopy(meal, day, user.dailyCalorieTarget),
    run: () => onFood(meal, now.date),
  };
  return <div className="quick home-quick"><QuickCard action={workout} kind="workout" onHold={onHold} /><QuickCard action={food} kind="food" onHold={onHold} /></div>;
}
